/**
 * PA — Market on/off registry (migration 804).
 *
 * Turn a market ON to offer it to agents and activate its listing-verification
 * triggers; OFF hides it from the composer and suppresses those triggers. Launch
 * config is Lebanon-only. Consumes GET/PUT /api/admin/markets (platform-admin).
 */
import { useCallback, useEffect, useState } from 'react'
import { Loader2, RefreshCw } from 'lucide-react'
import { api, type AdminMarket } from '@/api/client'
import { useToast } from '@/components/ui/toast'
import { usePageTitle } from '@/lib/usePageTitle'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

interface MarketRow extends AdminMarket {
  phone_number_id: string | null
  display_name: string | null
}

interface SenderDraft {
  phone_number_id: string
  display_name: string
}

function draftsFrom(markets: MarketRow[]): Record<string, SenderDraft> {
  return Object.fromEntries(markets.map((m) => [m.code, {
    phone_number_id: m.phone_number_id || '',
    display_name: m.display_name || '',
  }]))
}

export function MarketsAdminPage() {
  usePageTitle('Markets')
  const { addToast } = useToast()
  const [markets, setMarkets] = useState<MarketRow[]>([])
  const [drafts, setDrafts] = useState<Record<string, SenderDraft>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await api.getAdminMarkets()
      const rows = res.markets.map((m) => ({
        ...m,
        phone_number_id: m.phone_number_id || null,
        display_name: m.display_name || null,
      }))
      setMarkets(rows)
      setDrafts(draftsFrom(rows))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load markets')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  async function toggle(row: MarketRow) {
    const next = !row.enabled
    setSaving(row.code)
    // optimistic
    setMarkets((prev) => prev.map((m) => (m.code === row.code ? { ...m, enabled: next } : m)))
    try {
      await api.setAdminMarket(row.code, { enabled: next })
      addToast({
        title: `${row.label} ${next ? 'enabled' : 'disabled'}.`,
        variant: 'success',
      })
    } catch (err) {
      // revert
      setMarkets((prev) => prev.map((m) => (m.code === row.code ? { ...m, enabled: row.enabled } : m)))
      addToast({
        title: 'Could not update market',
        description: err instanceof Error ? err.message : 'Try again',
        variant: 'error',
      })
    } finally {
      setSaving(null)
    }
  }

  async function saveSender(row: MarketRow) {
    const draft = drafts[row.code] || { phone_number_id: '', display_name: '' }
    setSaving(row.code)
    try {
      const saved = await api.setAdminMarket(row.code, {
        phone_number_id: draft.phone_number_id,
        display_name: draft.display_name,
      })
      const next = {
        ...row,
        ...saved,
        phone_number_id: saved.phone_number_id || null,
        display_name: saved.display_name || null,
      }
      setMarkets((prev) => prev.map((m) => (m.code === row.code ? next : m)))
      setDrafts((prev) => ({
        ...prev,
        [row.code]: {
          phone_number_id: next.phone_number_id || '',
          display_name: next.display_name || '',
        },
      }))
      addToast({ title: `${row.label} WhatsApp number saved.`, variant: 'success' })
    } catch (err) {
      addToast({
        title: 'Could not save WhatsApp number',
        description: err instanceof Error ? err.message : 'Try again',
        variant: 'error',
      })
    } finally {
      setSaving(null)
    }
  }

  const enabledCount = markets.filter((m) => m.enabled).length

  return (
    <div className="mx-auto max-w-4xl px-[var(--lc-space-md)] py-[var(--lc-space-xl)]">
      <div className="mb-[var(--lc-space-xl)] flex items-start justify-between gap-4">
        <div>
          <h1 className="text-[length:var(--lc-type-heading-1)] font-semibold text-[var(--lc-text-heading)]">
            Markets
          </h1>
          <p className="mt-1 text-[length:var(--lc-type-body-sm)] text-[var(--lc-text-muted)]">
            Turn a market on to offer it to agents and activate its listing-verification
            requirements. Off hides it from the composer and suppresses those triggers.
            The WhatsApp sender follows the agent&apos;s market. Leave the phone number
            id empty to use the platform default.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void load()} className="gap-1.5">
          <RefreshCw className="h-4 w-4" /> Refresh
        </Button>
      </div>

      {loading ? (
        <div className="flex min-h-[30vh] items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-[var(--lc-text-muted)]" />
        </div>
      ) : error ? (
        <div
          role="alert"
          className="rounded-[var(--lc-radius-md)] border border-[var(--lc-status-unpublished-fg)] bg-[var(--lc-status-unpublished-bg)] px-3 py-2 text-[length:var(--lc-type-body-sm)] text-[var(--lc-status-unpublished-fg)]"
        >
          {error}
        </div>
      ) : (
        <>
          <p className="mb-3 text-[length:var(--lc-type-caption)] text-[var(--lc-text-muted)]">
            {enabledCount} of {markets.length} markets on
          </p>
          <ul className="space-y-2">
            {markets.map((row) => (
              <li
                key={row.code}
                className="rounded-[var(--lc-radius-lg)] border border-[var(--lc-border)] bg-[var(--lc-surface-raised)] p-4"
              >
                <div className="flex items-center justify-between gap-4">
                <div>
                  <span className="font-semibold text-[var(--lc-text-heading)]">{row.label}</span>
                  <span className="ml-2 text-[length:var(--lc-type-caption)] text-[var(--lc-text-muted)]">
                    {row.code}
                  </span>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={row.enabled}
                  aria-label={`${row.enabled ? 'Disable' : 'Enable'} ${row.label}`}
                  disabled={saving === row.code}
                  onClick={() => void toggle(row)}
                  className={cn(
                    'relative inline-flex h-6 w-11 shrink-0 items-center rounded-[var(--lc-radius-pill)] transition-colors',
                    row.enabled
                      ? 'bg-[var(--lc-action-primary)]'
                      : 'bg-[var(--lc-surface-sunken)] border border-[var(--lc-border-strong)]',
                    saving === row.code && 'opacity-60',
                  )}
                >
                  <span
                    className={cn(
                      'inline-block h-5 w-5 transform rounded-full bg-[var(--lc-surface)] shadow transition-transform',
                      row.enabled ? 'translate-x-5' : 'translate-x-0.5',
                    )}
                  />
                </button>
              </div>
              <div className="mt-3 grid gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
                <label className="block text-[length:var(--lc-type-caption)] text-[var(--lc-text-muted)]">
                  Phone number ID
                  <Input
                    className="mt-1"
                    inputMode="numeric"
                    value={drafts[row.code]?.phone_number_id ?? ''}
                    aria-label={`${row.label} WhatsApp phone number ID`}
                    placeholder="Cloud API id"
                    onChange={(e) => setDrafts((prev) => ({
                      ...prev,
                      [row.code]: {
                        phone_number_id: e.target.value,
                        display_name: prev[row.code]?.display_name ?? '',
                      },
                    }))}
                  />
                </label>
                <label className="block text-[length:var(--lc-type-caption)] text-[var(--lc-text-muted)]">
                  Display name
                  <Input
                    className="mt-1"
                    value={drafts[row.code]?.display_name ?? ''}
                    aria-label={`${row.label} WhatsApp display name`}
                    placeholder="WingCaster Lebanon"
                    onChange={(e) => setDrafts((prev) => ({
                      ...prev,
                      [row.code]: {
                        phone_number_id: prev[row.code]?.phone_number_id ?? '',
                        display_name: e.target.value,
                      },
                    }))}
                  />
                </label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={saving === row.code}
                  onClick={() => void saveSender(row)}
                >
                  Save number
                </Button>
              </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  )
}
